/**
 * QuestionConversionHint — Sprint B P0.1 implementation.
 *
 * Detects: plaintext paragraph ending with `?`, NOT inside a section,
 * and the post has ≥2 notify_authors. Shows an inline hint offering
 * conversion to a Question-Section with explicit targets-picker.
 *
 * Recycles existing A.1 `targets` routing → no new server-side surface.
 * Design-Spike in docs/AUTO_QUESTION_SECTION.md (Codex+Claude consensus
 * a-59efd659af2a).
 */

import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { CONTENT_API } from '../config'

interface Props {
  editor: Editor | null
  postId: number
  token: string
  notifyAuthors: string[]
  /** Display name of the current writer — used as section `initiator` attr. */
  initiatorName: string
  /** Called after a successful conversion so the parent can refresh. */
  onConverted?: (sectionId: string) => void
}

interface Candidate {
  text: string
  paragraphIndex: number
  /** DOM rect of the paragraph for hint-positioning. */
  rect: DOMRect
}

const MIN_LENGTH = 10
const DEBOUNCE_MS = 2000

function findCandidate(editor: Editor): Candidate | null {
  const { from } = editor.state.selection
  // Walk top-level nodes, find:
  //   - cursorParaIdx: the paragraph the cursor is in right now
  //   - all paragraphs (so we can check cursor's predecessor)
  const paragraphs: Array<{
    pIdx: number
    start: number
    text: string
    attrs: Record<string, unknown>
  }> = []
  let cursorParaIdx = -1
  let i = 0
  editor.state.doc.forEach((node, offset) => {
    const start = offset
    const end = offset + node.nodeSize
    paragraphs.push({
      pIdx: i,
      start,
      text: node.textContent.trim(),
      attrs: (node.attrs || {}) as Record<string, unknown>,
    })
    if (from >= start && from <= end) cursorParaIdx = i
    i++
  })

  // Detection rule: check the cursor's CURRENT paragraph first; if it's
  // empty (typical right after pressing Enter), fall back to the
  // immediately PRECEDING paragraph. That's where the question with `?`
  // usually lives — bug-fix 2026-06-05 after Alex' 3+ tests where Enter
  // moved the cursor past the question and detection found nothing.
  const candidates: Array<typeof paragraphs[number]> = []
  if (cursorParaIdx >= 0) {
    const cur = paragraphs[cursorParaIdx]
    if (cur.text.length > 0) candidates.push(cur)
    else if (cursorParaIdx > 0) candidates.push(paragraphs[cursorParaIdx - 1])
  }

  for (const p of candidates) {
    if (!p.text.endsWith('?')) continue
    if (p.text.length < MIN_LENGTH) continue
    if (p.attrs.section_id) continue
    const dom = editor.view.nodeDOM(p.start) as HTMLElement | null
    if (!dom || !dom.getBoundingClientRect) continue
    return {
      text: p.text,
      paragraphIndex: p.pIdx,
      rect: dom.getBoundingClientRect(),
    }
  }
  return null
}

export function QuestionConversionHint({
  editor,
  postId,
  token,
  notifyAuthors,
  initiatorName,
  onConverted,
}: Props) {
  const [candidate, setCandidate] = useState<Candidate | null>(null)
  const [expanded, setExpanded] = useState(false)
  // UX-Feedback Alex 2026-06-05: default ALL notify_authors checked, not
  // empty. Most common case is "ask everyone who's subscribed"; user
  // unchecks when they want to narrow. Saves N clicks pro Frage.
  const [selectedTargets, setSelectedTargets] = useState<string[]>(notifyAuthors)
  const [submitting, setSubmitting] = useState(false)
  const debounceRef = useRef<number | null>(null)

  // Detection: after editor inactivity, re-evaluate cursor's paragraph.
  // Only relevant when there are >=2 notify_authors (no pile-on risk
  // otherwise).
  useEffect(() => {
    if (!editor) return
    if (notifyAuthors.length < 2) return

    const evaluate = () => {
      const c = findCandidate(editor)
      setCandidate(c)
      // If user moved away from the candidate paragraph, collapse the
      // expanded form too — they probably decided not to convert.
      if (!c) {
        setExpanded(false)
        setSelectedTargets([])
      }
    }

    const debounced = () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current)
      }
      debounceRef.current = window.setTimeout(evaluate, DEBOUNCE_MS)
    }

    editor.on('update', debounced)
    editor.on('blur', evaluate)
    editor.on('selectionUpdate', debounced)
    return () => {
      editor.off('update', debounced)
      editor.off('blur', evaluate)
      editor.off('selectionUpdate', debounced)
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current)
      }
    }
  }, [editor, notifyAuthors.length])

  if (!candidate || !editor) return null

  const submit = async () => {
    if (selectedTargets.length === 0) {
      alert('Bitte mindestens einen Empfänger auswählen.')
      return
    }
    setSubmitting(true)
    try {
      // P0.1 main-wrap: server-side wraps Question initial_text with
      // multiline <main>...</main> automatically (sections.py:834-840).
      // Frontend MUST NOT pre-wrap or we'd get nested <main><main>...
      // (Codex Re-Test 2026-06-05 a-8157689288ad). Just send raw text.
      const initialText = candidate.text
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'question',
            attrs: {
              initiator: initiatorName,
              targets: selectedTargets.join(','),
              status: 'open',
            },
            initial_text: initialText,
            paragraph_index: candidate.paragraphIndex,
          }),
        },
      )
      if (!r.ok) {
        const errText = await r.text()
        alert(`Section konnte nicht angelegt werden: ${r.status} ${errText}`)
        return
      }
      const data = (await r.json()) as { id?: string }
      // Delete the original plaintext paragraph. Codex Annotation
      // a-8157689288ad: previously we ignored a non-2xx response →
      // doubled content. Now: surface the error to the user. Note:
      // the section_create insert pushes paragraphs down, so the
      // original plaintext is now at index + paragraphs-inserted.
      const deleteResp = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/crdt/apply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            mode: 'delete_paragraph',
            paragraph_index: candidate.paragraphIndex,
          }),
        },
      )
      if (!deleteResp.ok) {
        const delErr = await deleteResp.text()
        alert(
          `Section angelegt, aber Original-Paragraph konnte nicht gelöscht werden: ${deleteResp.status} ${delErr}. Bitte manuell entfernen um Doppelung zu vermeiden.`,
        )
        // Still call onConverted so the parent refreshes — the section
        // IS in the doc, it's only the cleanup that failed.
      }
      if (data.id) onConverted?.(data.id)
      setCandidate(null)
      setExpanded(false)
      setSelectedTargets([])
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSubmitting(false)
    }
  }

  const toggleTarget = (name: string) => {
    setSelectedTargets((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    )
  }

  // Position below the paragraph, anchored to its right edge to stay
  // out of the cursor's way.
  const top = candidate.rect.bottom + window.scrollY + 4
  const left = Math.min(
    candidate.rect.left + window.scrollX,
    window.innerWidth - 320,
  )

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        zIndex: 50,
        background: 'white',
        border: '1px solid #fbbf24',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        padding: 10,
        fontSize: 13,
        maxWidth: 320,
      }}
      onMouseDown={(e) => e.preventDefault() /* keep editor focus */}
    >
      {!expanded ? (
        <>
          <div style={{ marginBottom: 8, color: '#92400e' }}>
            <strong>Frage erkannt.</strong> Als Question-Section anlegen,
            damit gezielt jemand antwortet (kein Pile-on)?
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => {
                setExpanded(true)
                // Pre-select everyone — Alex' UX call: default is
                // "ask all", user unchecks to narrow.
                setSelectedTargets(notifyAuthors)
              }}
              style={{
                padding: '4px 10px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Ja, Section anlegen
            </button>
            <button
              type="button"
              onClick={() => setCandidate(null)}
              style={{
                padding: '4px 10px',
                background: 'white',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nein, lassen
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 8, color: '#374151' }}>
            <strong>Wen fragst du?</strong>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginBottom: 8,
              maxHeight: 160,
              overflowY: 'auto',
            }}
          >
            {notifyAuthors.map((name) => (
              <label
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedTargets.includes(name)}
                  onChange={() => toggleTarget(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || selectedTargets.length === 0}
              style={{
                padding: '4px 10px',
                background:
                  submitting || selectedTargets.length === 0
                    ? '#d1d5db'
                    : '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 12,
                cursor:
                  submitting || selectedTargets.length === 0
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {submitting ? 'Lege an…' : 'Anlegen'}
            </button>
            <button
              type="button"
              onClick={() => {
                setExpanded(false)
                // Reset to "all selected" so next open is fresh.
                setSelectedTargets(notifyAuthors)
              }}
              style={{
                padding: '4px 10px',
                background: 'white',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
          </div>
        </>
      )}
    </div>
  )
}
